import {
  FlexRow,
  FullContainer,
  getFileNameFromPath,
  isValidImage,
  Loading,
  StyledButton,
  useLoading,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { ChevronLeft, ChevronRight } from 'baseui/icon';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'baseui/modal';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { getS3DownloadUrlByKey, getS3FileUrlByKey } from '../../services/appService';
import { downloadBlob } from '../../utils/files';

type RenderMediaProps = {
  imageUrl?: string;
  cursor: string;
};

type Props = Readonly<{
  initial: string;
  s3Keys: string[];
  onClose: () => void;
  isOpen: boolean;
  header?: React.ReactNode;
  disableDownload?: boolean;
  renderMedia?: (props: RenderMediaProps) => React.ReactNode;
}>;

function ImagesCarouselFullScreenPreviewModal({
  initial,
  isOpen,
  onClose,
  s3Keys,
  disableDownload,
  header,
  renderMedia,
}: Props) {
  const { css, theme, classes, concatenateClasses } = useStyles();
  const { startLoading: startDownloading, stopLoading: stopDownloading, loading: downloading } = useLoading(false);
  const { startLoading, stopLoading, loading } = useLoading(true);
  const { showFailFeedback } = useFeedback();
  const [imageUrl, setImageUrl] = useState<string>();
  const [inViewImage, setInViewImage] = useState(initial);
  const mediaStyles = css({
    height: '100%',
    maxWidth: '100%',
    objectFit: 'contain',
  });
  const modalBodyStyles = css({
    flex: 1,
  });
  const modalFooterStyles = css({
    marginLeft: '0 !important',
    marginRight: '0 !important',
    marginTop: '0 !important',
    paddingLeft: theme.sizing.scale800,
    paddingRight: theme.sizing.scale800,
    borderTop: `${theme.borders.border200.borderWidth} ${theme.borders.border200.borderStyle} ${theme.borders.border200.borderColor}`,
  });

  const carouselData = useMemo(() => {
    const imageIndex = s3Keys.indexOf(inViewImage);
    if (imageIndex === -1 || s3Keys.length === 0) {
      return undefined;
    }
    const nextIndexRaw = imageIndex + 1;
    const prevIndexRaw = imageIndex - 1;
    const imagesLength = s3Keys.length;
    const nextIndex = nextIndexRaw > imagesLength - 1 ? 0 : nextIndexRaw;
    const prevIndex = prevIndexRaw < 0 ? imagesLength - 1 : prevIndexRaw;
    return {
      nextCursor: s3Keys[nextIndex],
      prevCursor: s3Keys[prevIndex],
      cursor: s3Keys[imageIndex],
    };
  }, [inViewImage, s3Keys]);

  useEffect(() => {
    const loadMedia = async () => {
      if (!inViewImage) {
        stopLoading();
        return;
      }
      startLoading();
      setImageUrl(undefined);
      const { url, error } = await getS3FileUrlByKey(inViewImage);
      if (!error && url) {
        setImageUrl(url);
      }
      stopLoading();
    };

    loadMedia();
  }, [inViewImage]);

  const download = async () => {
    if (!carouselData) return;
    startDownloading();
    const { file, error } = await getS3DownloadUrlByKey(carouselData.cursor);
    if (error) {
      showFailFeedback(error);
    } else if (file) {
      downloadBlob(file, getFileNameFromPath(carouselData.cursor));
    }
    stopDownloading();
  };

  const goToPrevious = () => {
    if (!carouselData) {
      return;
    }
    setInViewImage(carouselData.prevCursor);
  };

  const goToNext = () => {
    if (!carouselData) {
      return;
    }
    setInViewImage(carouselData.nextCursor);
  };

  return createPortal(
    <Modal
      animate
      closeable
      onClose={onClose}
      isOpen={isOpen}
      size="full"
      overrides={{
        Dialog: {
          style: {
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {header && <ModalHeader>{header}</ModalHeader>}
      <ModalBody className={modalBodyStyles}>
        <AutoSizer>
          {({ width, height }) => (
            <FlexRow
              classNames={concatenateClasses(
                classes.centeredBetween,
                css({
                  width: `${width}px`,
                  height: `${height}px`,
                }),
              )}
            >
              <Button
                size="compact"
                shape="circle"
                kind="tertiary"
                onClick={goToPrevious}
              >
                <ChevronLeft
                  size={28}
                  title="Anterior"
                />
              </Button>
              <FullContainer
                flexible
                classNames={css({
                  justifyContent: 'center',
                  position: 'relative',
                })}
              >
                {loading && <Loading />}
                {!loading &&
                  (renderMedia
                    ? renderMedia({
                        imageUrl,
                        cursor: carouselData?.cursor ?? inViewImage,
                      })
                    : imageUrl &&
                      isValidImage(imageUrl) && (
                        <img
                          src={imageUrl}
                          alt=""
                          className={mediaStyles}
                        />
                      ))}
              </FullContainer>
              <Button
                size="compact"
                shape="circle"
                kind="tertiary"
                onClick={goToNext}
              >
                <ChevronRight
                  size={28}
                  title="Siguiente"
                />
              </Button>
            </FlexRow>
          )}
        </AutoSizer>
      </ModalBody>
      <ModalFooter className={modalFooterStyles}>
        <FlexRow
          gap={theme.sizing.scale300}
          classNames={concatenateClasses(
            classes.centeredEnd,
            css({
              maxWidth: '500px',
              margin: '0 auto',
            }),
          )}
        >
          <StyledButton
            kind="secondary"
            onClick={onClose}
            full
          >
            Cerrar
          </StyledButton>
          {!disableDownload && (
            <StyledButton
              onClick={download}
              isLoading={downloading}
              full
            >
              Descargar foto
            </StyledButton>
          )}
        </FlexRow>
      </ModalFooter>
    </Modal>,
    document.body,
  );
}

export default ImagesCarouselFullScreenPreviewModal;
